import background from '@assets/search-result-type/background.webp';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { getSearchTypeOption, SearchTypeOptionData } from '@lib/utils/searchTypeOptions';
import ScienceIcon from '@mui/icons-material/Science';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { SearchModeBadge } from '../SearchModeBadge/SearchModeBadge';
import css from './ResultTypeTooltip.module.scss';

export type TooltipBadgeVariant = 'neutral' | 'warning' | 'error';

export interface TooltipBadge {
  variant: TooltipBadgeVariant;
  label: string;
  icon: ReactNode;
}

/** Default badge for the existing "Experimental" case. Exported so
 *  callers can fall back to it explicitly when they're not overriding. */
export const EXPERIMENTAL_BADGE: TooltipBadge = {
  variant: 'neutral',
  label: 'Experimental',
  icon: <ScienceIcon />,
};

type CustomInnerTooltipProps = Pick<
  SearchTypeOptionData,
  'title' | 'subtitle' | 'visual' | 'conversational' | 'examplePrompt'
> & {
  badge: TooltipBadge | null;
};

const TINT_VARIANT_CLASS: Record<TooltipBadgeVariant, string> = {
  neutral: css.tintNeutral,
  warning: css.tintWarning,
  error: css.tintError,
};

// Full-opacity variants of each badge color, used to tint the
// tooltip's outer 1px border so the whole tooltip reads as the same
// state as the ribbon (instead of a colored ribbon inside a purple
// outline).
const BORDER_COLOR_BY_VARIANT: Record<TooltipBadgeVariant, string> = {
  neutral: 'rgb(208, 169, 239)',
  warning: 'rgb(203, 103, 69)',
  error: 'rgb(214, 69, 80)',
};

const CustomInnerTooltip = ({
  badge,
  title,
  subtitle,
  examplePrompt,
  visual,
  conversational,
}: CustomInnerTooltipProps) => {
  return (
    <div className={css.tooltip}>
      <SearchModeBadge badge={badge} className={css.tooltipBadge} />
      <div className={classNames(css.imageWrapper, badge && TINT_VARIANT_CLASS[badge.variant])}>
        <img
          src={background}
          alt=""
          className={classNames(css.image, { [css.opaqueImage]: !visual })}
        />
        {conversational && <div className={css.speechBubble}>This stage is my only home!</div>}
      </div>
      <div className={css.header}>
        <span className={css.title}>{title}</span>
        <span className={css.subtitle}>{subtitle}</span>
      </div>
      <div className={css.examplePrompt}>
        <span className={css.title}>Example prompt: </span>
        {examplePrompt.map((item, index) => (
          <span key={index} className={item.type === 'incorrect' ? css.incorrect : css.correct}>
            {item.text}
          </span>
        ))}
      </div>
    </div>
  );
};

type ResultTypeTooltipProps = Omit<CustomInnerTooltipProps, 'badge'> & {
  children: React.ReactElement;
  anchorEl?: HTMLElement | (() => HTMLElement) | null;
  badge: TooltipBadge | null;
};

const ResultTypeTooltip = ({ children, anchorEl, badge, ...props }: ResultTypeTooltipProps) => {
  const borderColor = BORDER_COLOR_BY_VARIANT[badge?.variant ?? 'neutral'];
  const isMobile = useIsMobile();
  // Mobile opens the drawer on tap instead; suppress the tooltip so
  // it doesn't fire on long-press or on a simulated-touch viewport
  // that still emits mouse events.
  if (isMobile) return children;
  return (
    <Tooltip
      title={<CustomInnerTooltip badge={badge} {...props} />}
      placement="top-start"
      slotProps={{
        popper: {
          anchorEl: anchorEl ?? undefined,
        },
        tooltip: {
          sx: {
            padding: 0,
            borderRadius: '20px',
            border: `1px solid ${borderColor}`,
            backgroundColor: 'rgba(30, 30, 30, 0.9)',
            color: 'white',
            animationTimingFunction: 'linear',
            animationDuration: '300ms',
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

type SearchTypeTooltipWrapperProps = {
  children: React.ReactElement;
  anchorEl?: HTMLElement | (() => HTMLElement) | null;
  /**
   * Overrides the default badge computed from the search-type data
   * (only `unified` has a default "Experimental" badge today).
   * Callers surface scope-driven variants like `warning` (some
   * libraries audio-centric) or `error` (all libraries audio-centric).
   * Pass `null` to suppress the default badge explicitly.
   */
  badge?: TooltipBadge | null;
};

const makeSearchTypeTooltip = (type: 'visual' | 'transcript' | 'unified') => {
  const Component = ({ children, anchorEl, badge }: SearchTypeTooltipWrapperProps) => {
    const data = getSearchTypeOption(type)!;
    const resolvedBadge =
      badge !== undefined ? badge : data.experimental ? EXPERIMENTAL_BADGE : null;
    return (
      <ResultTypeTooltip
        title={data.title}
        visual={data.visual}
        conversational={data.conversational}
        subtitle={data.subtitle}
        anchorEl={anchorEl}
        examplePrompt={data.examplePrompt}
        badge={resolvedBadge}
      >
        {children}
      </ResultTypeTooltip>
    );
  };
  Component.displayName = `${type.charAt(0).toUpperCase() + type.slice(1)}SearchTooltip`;
  return Component;
};

export const VisualSearchTooltip = makeSearchTypeTooltip('visual');
export const ConversationalSearchTooltip = makeSearchTypeTooltip('transcript');
export const UnifiedSearchTooltip = makeSearchTypeTooltip('unified');
